import { Box, Button, Link, TextField, Typography, useTheme, alpha } from '@mui/material';
import DOMPurify from 'dompurify';
import { z as zod } from 'zod';
import React, { useState } from 'react';
import loginHero from '../assets/loginHero.png';
import navBarSurePassLogo from '../assets/navBarSurePassLogo.svg';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';

const InitialLogin = () => {
  const theme = useTheme();
  const [email, setEmail] = useState('');
  const { loginWithRedirect } = useAuth0();

  const emailSchema = zod.string().email('Invalid email address');

  const checkValidEmail = (submittedEmail) => {
    return emailSchema.safeParse(submittedEmail).success;
  };

  const getConnectionName = async (): Promise<string> => {
    const axiosInstance = axios.create();
    const resp = await axiosInstance.get(
      `${import.meta.env.VITE_API_SERVER_URL}/api/v2/auth/connection`,
      {
        params: { email: email }
      }
    );
    if (resp.status !== 200) {
      return 'email';
    }
    return resp.data.connection;
  };

  const redirectToAuth0 = async () => {
    const connectionName = await getConnectionName();
    loginWithRedirect({
      authorizationParams: {
        connection: connectionName,
        login_hint: email
      },
      appState: {
        silentAttempt: false
      }
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setEmail(DOMPurify.sanitize(value));
  };

  const handleSubmit = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    redirectToAuth0();
  };

  return (
    <Box
      component="main"
      sx={{
        display: 'flex',
        height: '100vh',
        width: '100vw',
        overflow: 'hidden',
        backgroundColor: theme.palette.primary.dark,
        position: 'relative',
      }}
    >
      {/* Left: photo panel with diagonal clip */}
      <Box
        sx={{
          display: { xs: 'none', md: 'block' },
          position: 'absolute',
          top: 0,
          left: 0,
          width: '55%',
          height: '100%',
          clipPath: 'polygon(0 0, 100% 0, 85% 100%, 0 100%)',
          zIndex: 1,
        }}
      >
        <Box
          component="img"
          src={loginHero}
          alt="Person using SurePass verification kiosk"
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'left center',
            display: 'block',
          }}
        />
      </Box>

      {/* Right: dark content panel */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: { xs: '100%', md: '55%' },
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: { xs: 'center', md: 'flex-end' },
          zIndex: 0,
          pr: { xs: 4, md: 8 },
          pl: { xs: 4, md: 0 },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: { xs: 'center', md: 'flex-end' },
            gap: 3,
            maxWidth: '380px',
            width: '100%',
          }}
        >
          {/* Logo */}
          <Box
            component="img"
            src={navBarSurePassLogo}
            alt="SurePass logo"
            sx={{ width: '180px', height: 'auto' }}
          />

          {/* Title */}
          <Typography
            variant="h2"
            sx={{
              color: theme.palette.background.paper,
              fontWeight: 300,
              textAlign: { xs: 'center', md: 'right' },
              lineHeight: 1.15,
            }}
          >
            Authority
            <br />
            Verification
          </Typography>

          {/* Subtitle */}
          <Typography
            component="h1"
            variant="h5"
            sx={{
              color: theme.palette.background.paper,
              fontWeight: 400,
              textAlign: { xs: 'center', md: 'right' },
            }}
          >
            SurePass Admin Portal
          </Typography>

          {/* Email field */}
          <TextField
            onChange={handleChange}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && checkValidEmail(email)) {
                redirectToAuth0();
              }
            }}
            label="Email Address"
            placeholder="name@example.com"
            fullWidth
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{
              '& input': { color: theme.palette.background.paper },
              '& .MuiInputLabel-outlined': { color: theme.palette.background.paper },
              '& .MuiInputLabel-outlined.Mui-focused': { color: theme.palette.background.paper },
              '& .MuiOutlinedInput-root fieldset': {
                borderColor: alpha(theme.palette.background.paper, 0.7),
              },
              '& .MuiOutlinedInput-root:hover fieldset': {
                borderColor: theme.palette.background.paper,
              },
              '& .MuiOutlinedInput-root.Mui-focused fieldset': {
                borderColor: theme.palette.background.paper,
              },
            }}
          />

          {/* Terms */}
          <Typography
            variant="caption"
            sx={{
              color: alpha(theme.palette.background.paper, 0.85),
              textAlign: { xs: 'center', md: 'right' },
              lineHeight: 1.6,
            }}
          >
            By utilizing the SurePass service, you agree
            <br />
            to our{' '}
            <Link
              href="https://surepass.com/terms"
              sx={{
                color: theme.palette.background.paper,
                fontWeight: 600,
                textDecoration: 'underline',
                '&:hover': { color: alpha(theme.palette.background.paper, 0.65) },
              }}
            >
              Terms of Use
            </Link>{' '}
            and{' '}
            <Link
              href="https://surepass.com/privacy/"
              sx={{
                color: theme.palette.background.paper,
                fontWeight: 600,
                textDecoration: 'underline',
                '&:hover': { color: alpha(theme.palette.background.paper, 0.65) },
              }}
            >
              Privacy Policy
            </Link>
            .
          </Typography>

          {/* Submit button */}
          <Button
            onClick={(e) => handleSubmit(e)}
            disabled={!checkValidEmail(email)}
            variant="contained"
            sx={{
              px: 5,
              py: 1,
              borderRadius: 1,
              fontWeight: 700,
              letterSpacing: '0.08em',
              alignSelf: { xs: 'center', md: 'flex-end' },
              backgroundColor: theme.palette.info.light,
              color: theme.palette.text.primary,
              '&.Mui-disabled': {
                backgroundColor: alpha(theme.palette.info.light, .4)
              },
            }}
          >
            SUBMIT
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default InitialLogin;
